import { useEffect, useState } from 'react';
import './EventActiveModules.scss';
import { BizCheckBox } from '../../Core/Components/Mini/BizCheckBox';
import { BizButton } from '../../Core/Components/Mini/BizButton';
import { useBizMutation } from '../../Core/Hooks/useBizQuery';
import {
	updateEventActiveModules,
	getEventActiveModules,
	getAvailableModulesForEvent,
	type ModuleOption,
} from '../../Core/Services/admin/eventActiveModules';
import { useInvalidateOneEventCache } from '../../Core/Services/event';
import { useTranslation } from 'react-i18next';
import { EventActiveModuleKey } from '../../Core/Types/event';

type EventActiveModulesProps = {
	eventId: number;
	onClose?: () => void;
};

const EventActiveModules = ({ eventId, onClose }: EventActiveModulesProps) => {
	const { t } = useTranslation();
	const { invalidateOneEventCache } = useInvalidateOneEventCache();
	const [availableModules, setAvailableModules] = useState<ModuleOption[]>([]);
	const [activeModules, setActiveModules] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);

	const { mutate: saveActiveModules, statusState: saveStatusState } = useBizMutation(updateEventActiveModules);

	useEffect(() => {
		//TODO MOVE TO HOOKS
		const loadData = async () => {
			setLoading(true);
			try {
				const [modules, active] = await Promise.all([getAvailableModulesForEvent(), getEventActiveModules(eventId)]);
				setAvailableModules(modules);
				setActiveModules(active);
			} catch (error) {
				console.error('Failed to load event active modules data:', error);
			} finally {
				setLoading(false);
			}
		};

		if (eventId > 0) {
			loadData();
		}
	}, [eventId]);

	useEffect(() => {
		if (saveStatusState === 'SUCCESS') {
			invalidateOneEventCache(eventId);
			if (onClose) {
				onClose();
			}
		}
	}, [saveStatusState]);

	const handleModuleToggle = (moduleKey: EventActiveModuleKey | string, checked: boolean) => {
		setActiveModules((prev) => {
			if (checked) {
				return [...prev, moduleKey];
			} else {
				return prev.filter((m) => m !== moduleKey);
			}
		});
	};

	const handleSave = () => {
		saveActiveModules({ eventId, activeModules });
	};

	const handleCancel = () => {
		if (onClose) {
			onClose();
		}
	};

	if (loading) {
		return (
			<div className='biz-event-active-modules'>
				<p>{t('EVENT.ACTIVE_MODULES.LOADING', { defaultValue: 'Loading...' })}</p>
			</div>
		);
	}

	return (
		<div className='biz-event-active-modules'>
			<div className='active-modules-content'>
				<div className='active-modules'>
					<p>
						{t('EVENT.ACTIVE_MODULES.SELECT_MODULES', {
							defaultValue: 'Select which modules should be available for this event:',
						})}
					</p>

					{availableModules.map((module) => (
						<div key={module.Key} className={module.IsEnabled ? '' : 'margin-top'}>
							{module.IsEnabled ? (
								<BizCheckBox
									value={activeModules.includes(module.Key)}
									onChange={(checked) => handleModuleToggle(module.Key, checked)}
									label={module.Name}
								/>
							) : (
								<div className='disabled-module'>
									<BizCheckBox value={false} onChange={() => {}} label={module.Name} disabled={true} />
									<span className='disabled-reason'>
										{t('EVENT.ACTIVE_MODULES.DISABLED_IN_PORTAL_SETTINGS', {
											defaultValue: '(Disabled in portal settings)',
										})}
									</span>
								</div>
							)}
						</div>
					))}
				</div>

				<div className='dialog-btn-actions'>
					<BizButton
						text={t('GLOBAL.SAVE', { defaultValue: 'Save' })}
						onClick={handleSave}
						state={saveStatusState}
						isLead={true}
						legacy={false}
					/>
					{onClose && (
						<BizButton
							text={t('GLOBAL.CANCEL', { defaultValue: 'Cancel' })}
							onClick={handleCancel}
							className='margin-left'
							legacy={false}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default EventActiveModules;
